/**
 * Mermaid 다이어그램 lazy 렌더러 — chat-markdown 의 ```mermaid 블록에서 사용.
 *
 * UX:
 *   - 인라인: SVG max-width: 100% 로 카드 폭에 맞춤
 *   - 우상단 돋보기 버튼 hover 노출 → 라이트박스 모달 (휠 zoom, 드래그 pan, ESC/바깥 클릭 닫기)
 */
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, X, Plus, Minus, RotateCcw } from '../icons';

let mermaidPromise: Promise<typeof import('mermaid').default> | null = null;

async function getMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((m) => {
      const mermaid = m.default;
      mermaid.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'strict',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Pretendard, "Noto Sans KR", sans-serif',
      });
      return mermaid;
    });
  }
  return mermaidPromise;
}

let counter = 0;

export const MermaidBlock = memo(function MermaidBlock({ code }: { code: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [svg, setSvg] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const id = `chat-mermaid-${counter++}`;
    setError(null);
    // 깜빡임 방지: 재렌더(code 변경) 시에도 기존 SVG 를 비우지 않고(빈 프레임 X)
    // 새 SVG 가 준비되면 교체. (setSvg(null) 제거)
    getMermaid()
      .then(async (mermaid) => {
        if (cancelled) return;
        // 전체 페이지 세로 스크롤 깜빡임 방지: 컨테이너를 안 넘기면 mermaid 가 측정용 임시
        // <div><svg> 를 위치 스타일 없이 document.body 끝에 in-flow 로 붙이고, 비동기 draw()
        // (폰트 로드·getBBox·레이아웃) 동안 그대로 둔다 → 조직도 전체 높이만큼 body 가 늘어나
        // 페이지 세로 스크롤바가 떴다 사라진다. off-screen 컨테이너로 측정을 레이아웃에서 격리.
        // (getBBox 는 overflow:hidden·height:0 과 무관하게 geometry 를 반환하므로 측정 정확.)
        const host = document.createElement('div');
        host.setAttribute('aria-hidden', 'true');
        host.style.cssText =
          'position:absolute;left:-99999px;top:0;width:1200px;height:0;overflow:hidden;pointer-events:none';
        document.body.appendChild(host);
        try {
          const { svg: rendered } = await mermaid.render(id, code, host);
          if (cancelled) return;
          setSvg(rendered);
        } catch (e) {
          if (cancelled) return;
          setError(e instanceof Error ? e.message : String(e));
        } finally {
          host.remove();
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  useEffect(() => {
    if (ref.current && svg) ref.current.innerHTML = svg;
  }, [svg]);

  if (error) {
    return (
      <pre className="chat-md-mermaid-error">
        <code>{`mermaid render error: ${error}\n\n${code}`}</code>
      </pre>
    );
  }
  return (
    <div className="chat-md-mermaid">
      <button
        type="button"
        className="chat-md-mermaid-expand"
        aria-label="다이어그램 확대 보기"
        title="다이어그램 확대 보기"
        onClick={() => setLightboxOpen(true)}
        disabled={!svg}
      >
        <Maximize2 size={14} />
      </button>
      <div ref={ref} aria-label="mermaid diagram" />
      {lightboxOpen && svg && <MermaidLightbox svg={svg} onClose={() => setLightboxOpen(false)} />}
    </div>
  );
});

interface DragState {
  startX: number;
  startY: number;
  tx0: number;
  ty0: number;
}



function MermaidLightbox({ svg, onClose }: { svg: string; onClose: () => void }) {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<DragState | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  // 모달용 svg — 원본 그대로. 이전엔 id 충돌 회피 위해 prefix namespacing 적용했지만,
  // mermaid 가 svg 안 <style> 에 박는 CSS 의 hex 색상 (#fff, #333 등) 까지 ID selector 로
  // 오인해 변환 → 모든 색상 invalid 로 검은 일색 fallback 되는 부작용. ID 충돌은 marker 가
  // 거의 동일하므로 시각적 영향 미미 — 카드 안 외관 정확 일치를 우선.
  const svgForLightbox = svg;

  // body scroll lock + ESC 닫기
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // wheel zoom — passive listener 회피 위해 native event 직접 부착 (React onWheel 은 passive).
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const onWheelNative = (e: WheelEvent) => {
      e.preventDefault();
      setScale((s) => {
        const next = e.deltaY < 0 ? s * 1.15 : s / 1.15;
        return Math.max(0.3, Math.min(8, next));
      });
    };
    stage.addEventListener('wheel', onWheelNative, { passive: false });
    return () => stage.removeEventListener('wheel', onWheelNative);
  }, []);

  const reset = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, tx0: translate.x, ty0: translate.y };
    setIsDragging(true);
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current) return;
    setTranslate({
      x: dragRef.current.tx0 + (e.clientX - dragRef.current.startX),
      y: dragRef.current.ty0 + (e.clientY - dragRef.current.startY),
    });
  };
  const endDrag = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  return createPortal(
    <div
      className="chat-md-mermaid-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="다이어그램 확대 보기"
      onClick={onClose}
    >
      <div className="chat-md-mermaid-lightbox-toolbar" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          aria-label="축소"
          title="축소"
          onClick={() => setScale((s) => Math.max(0.3, s / 1.2))}
        >
          <Minus size={16} />
        </button>
        <span className="chat-md-mermaid-lightbox-scale">{Math.round(scale * 100)}%</span>
        <button
          type="button"
          aria-label="확대"
          title="확대"
          onClick={() => setScale((s) => Math.min(8, s * 1.2))}
        >
          <Plus size={16} />
        </button>
        <button
          type="button"
          aria-label="원본 크기"
          title="원본 크기"
          onClick={reset}
        >
          <RotateCcw size={14} />
        </button>
        <button
          type="button"
          aria-label="닫기"
          title="닫기"
          onClick={onClose}
        >
          <X size={18} />
        </button>
      </div>
      <div className="chat-md-mermaid-lightbox-hint" onClick={(e) => e.stopPropagation()}>
        휠: 확대/축소 · 드래그: 이동 · 더블 클릭: 원본 · ESC/바깥: 닫기
      </div>
      <div
        ref={stageRef}
        className={`chat-md-mermaid-lightbox-stage${isDragging ? ' is-dragging' : ''}`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onDoubleClick={reset}
      >
        <div
          className="chat-md-mermaid-lightbox-content"
          style={{ transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})` }}
          aria-label="mermaid diagram (확대 보기)"
          dangerouslySetInnerHTML={{ __html: svgForLightbox }}
        />
      </div>
    </div>,
    document.body,
  );
}
